import { CustomGridPageBase } from "../../common/custom-grid-page.base";
import {autoinject, transient, computedFrom, observable, BindingEngine} from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { DataType, SourceType, QueryFilter } from "../../../../models/queries/queries.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { StepModel } from "../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { Reflection } from "../../../../utilities/reflection";
import { ListItemModel, DeleteListRequest } from "../../../../models/lists/list.models";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../constants/authentication/claims";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { ListService } from "../../../../services/modules/list-client.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { LookupValueQuery } from "../../../../constants/modules/lookup.constants";
import { MobileHelper } from "../../../../helpers/mobile-helper";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { LookupValueRequest, LookupValueResponse } from "../../../../models/lookups/lookup.models";
import { Guid } from "../../../../utilities/guid";
import { ValidationResult } from "../../../../models/validations/validations.models";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";

@autoinject()
@transient()
export class Lists extends CustomGridPageBase {

    private initialPromise: Promise<void> = null;

    public canAdd: boolean;
    public isRightShipUser: boolean;
    public canEditProtectedList: boolean;
    public canImportListItems: boolean;
    public canEditLists: boolean;
    public canDeleteLists: boolean;
    public selectedListType: string;
    public canAccessListsActions: boolean = false;
    public canViewIsRightshipFilter: boolean = false;

    public businessUnitId: number;
    public customerId: number;
    public isCustomerAccount: boolean;

    public selectedFilter: { title: string, clear: () => void, apply: () => void };

    public isMobileFilterOpen: boolean;

    public tabs: StepModel[] = [
        {
            show: true,
            title: "VESSEL LIST",
            step: 1,
            onClick: () => {
                this.selectedListType = "vessel";
                this.filterClick(EntityTypes.Vessel);
            }
        },
        {
            show: true,
            title: "COMPANY LIST",
            step: 2,
            onClick: () => {
                this.selectedListType = "company";
                this.filterClick(EntityTypes.Company);
            }
        }
    ];

    constructor(i18N: I18N,
        protected bindingEngine: BindingEngine,
        private dialogClient: DialogClientService,
        private notificationClientService: NotificationClientService,
        private listService: ListService,
        userClaimsService: UserClaimsService,
        private customerService: CommonCustomerService,
        private userClientService: UserClientService,
        private lookupService: LookupClientService,
        private validationsClientService: ValidationsClientService
    ) {
        super(i18N, 'Lists', bindingEngine);
        this.canAdd = userClaimsService.hasPermission(Claims.CanAddLists);
        this.isRightShipUser = userClaimsService.hasPermission(Claims.RightShipUser);
        this.canEditProtectedList = userClaimsService.hasPermission(Claims.CanEditProtectedList);
        this.canAccessListsActions = userClaimsService.hasPermission(Claims.CanAccessListsActions);
        this.canImportListItems = userClaimsService.hasPermission(Claims.CanImportListItems);
        this.canEditLists = userClaimsService.hasPermission(Claims.CanEditLists);
        this.canDeleteLists = userClaimsService.hasPermission(Claims.CanDeleteLists);
        this.canViewIsRightshipFilter = userClaimsService.hasPermission(Claims.RightShipUser);
        this.customerId = this.userClientService.currentUser.settings.customerId;

        this.initialPromise = new Promise<void>((resolve, reject) => {
            const promises: Promise<any>[] = [
                this.customerService.getBusinessUnitId(),
                this.customerService.getCustomerId(),
                this.lookupService.getLookup(new LookupValueRequest(LookupValueQuery.Customers))
            ];
            Promise.all(promises).then(x => {
                this.businessUnitId = x[0];
                this.customerId = x[1];
                this.setPrivacies(x[2]);
                this.isCustomerAccount = !isNaN(this.businessUnitId) && this.businessUnitId > 0;
                this.setColumns();
                resolve();
                this.initialPromise = null;
            }, reject);
        });

        this.setDefaultFilters();
    }

    setDefaultFilters() {
        this.clearAllFilters(false);
    }

    setColumns() {
        const columns = [];

        columns.push(
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.CustomTemplate,
                isSortable: true,
                instancePerRow: true,
                name: 'name',
                title: 'Name',
                isAscending: true,
                value: '<div><a class="link" href="/list-items/${item.listId}">${item.name}</a><comp-icon class="icon-lock" icon="lock" if.bind="item.isInternal"></comp-icon>',
                parameter: {
                    item: null,
                },
                width: MobileHelper.isMobileDevice()  &&  window.innerWidth < 1024 ? "" : "200px",
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.Default,
                isSortable: true,
                name: 'description',
                title: 'Description',
                ellipsis: 100,
                width: MobileHelper.isMobileDevice() &&  window.innerWidth < 1024  ? "" : "200px",
            },
            {
                sortOrder: 0,
                columnOrder: 0,
                dataType: DataType.Default,
                isSortable: true,
                name: 'listGroup',
                title: 'Category'
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.Default,
                isSortable: true,
                name: 'businessUnit',
                title: 'Privacy',
                valueConverter: (x, item) => {
                    if (item && item.isPersonal) return "Private";
                    if (item && item.businessUnitId) return `${item.customer} (${item.businessUnit})`;
                    if (item && item.customerId) return item.customer;

                    return x || "RightShip System List";
                }
            },
            {
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.Number,
                isSortable: true,
                name: 'listItemsCount',
                valueConverter: (x) => {
                    return x && x > 0? x: "0";
                },
                title: 'Qty'
            },
            {
                columnOrder: 6,
                sortOrder: 6,
                dataType: DataType.Boolean,
                isSortable: true,
                name: 'isLinkedToVettingRule',
                title: 'Linked To Vetting Rule'
            },
            {
                columnOrder: 7,
                sortOrder: 7,
                dataType: DataType.CustomTemplate,
                isSortable: true,
                instancePerRow: true,
                name: 'lastModifiedDate',
                title: 'Last Modified',
                isAscending: true,
                value: '<div><b>${item.lastModifiedByName}</b><p style="font-size: 1.3rem;">${item.lastModifiedDate | dateFormat: "DD MMM YYYY"}</p></div>',
                parameter: {
                    item: null,
                }
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "menu",
                isHidden: !this.canAccessListsActions,
                value: `<div class="actions row justify-content-end align-items-middle">
                            <list-menu hide-menu-border="true" menu-items.bind="menuItems(item)">
                                <template replace-part="menu-item">
                                    <div class="row nowrap align-items-middle" click.trigger="menuItem.click($event, item)">
                                        <span class="text">\${menuItem.item.text}</span>
                                    </div>
                                </template>
                            </list-menu>
                        </div>`,
                parameter: {
                    item: null,
                    isRightShipUser: this.isRightShipUser,
                    menuItems: (item) => {
                        return [
                            {
                                isVisible:
                                    (this.canImportListItems &&
                                        (this.isRightShipUser || (item.businessUnitId === this.businessUnitId) || (item.customerId === this.customerId)))
                                    && (this.canEditProtectedList || !item.isInternal),
                                click: ($event, item) => {
                                    $event.stopPropagation();
                                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.ImportExcelDialog, {
                                        onComplete: () => {
                                            this.reload();
                                        },
                                        entityTypeId: EntityTypes.ListItem,
                                        filter: "list_" + item.listId,
                                        explicitValues: [
                                            {
                                                property: Reflection.unCamelCase(Reflection.getPropertyName<ListItemModel>(x => x.listId), false),
                                                value: item.listId
                                            }
                                        ]
                                    }, true, false, false, 'right');
                                },
                                item: { text: "Import" },
                                classList: ''
                            },
                            {
                                isVisible: true,
                                click: ($event, item) => {
                                    $event.stopPropagation();
                                    this.listService.exportList(item.listId, item.name);
                                },
                                item: { text: "Export" },
                                classList: ''
                            },
                            {
                                isVisible: true,
                                click: ($event, item) => {
                                    $event.stopPropagation();
                                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.ListImportsHistory, { listId: item.listId }, true, false, false, 'right');
                                },
                                item: { text: "Imports History" },
                                classList: ''
                            },
                            {
                                isVisible: () =>
                                    this.canEditLists &&
                                    (this.isRightShipUser || item.businessUnitId === this.businessUnitId || item.customerId === this.customerId) &&
                                    (!item.isInternal || this.canEditProtectedList),
                                click: ($event, item) => {
                                    $event.stopPropagation();
                                    this.editList(item);
                                },
                                item: { text: "Edit" },
                                classList: ''
                            },
                            {
                                isVisible: () =>
                                    this.canDeleteLists &&
                                    (this.isRightShipUser || item.businessUnitId === this.businessUnitId || item.customerId === this.customerId) &&
                                    (!item.isInternal || this.canEditProtectedList),
                                click: ($event, item) => {
                                    $event.stopPropagation();
                                    this.showDeleteDialog(item);
                                },
                                item: { text: "Remove" },
                                classList: ''
                            }
                        ]
                    }
                }
            });

        columns.forEach((column, index) => {
            column.columnOrder = index;
            column.sortOrder = index;
        });

        this.columns = columns;
        this.filter(EntityTypes.Vessel);
    }

    private reloadTimeout;
    debounceReload = (immediate: boolean = false, ...args) => {
        const func = this.reload;
        const wait = 100;

        const later = function (...args) {
            this.reloadTimeout = null;
            if (!immediate) func.apply(this, args);
        };

        clearTimeout(this.reloadTimeout);

        this.reloadTimeout = setTimeout(later, wait);

        if (immediate) func.apply(this, args);
    }

    reload() {
        const initialFilters = [];

        if (this.selectedFilters && this.selectedFilters.length > 0) {
            const selectedFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "entityTypeId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: false,
                },
                filterValue: this.selectedFilters || [],
                isAnd: false,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(selectedFilter);
        }

        if (this.searchFilter) {
            initialFilters.push(QueryFilter.getQuery("name", this.searchFilter, "contains({0}, '{1}')", DataType.Default, true, false, null, null, true));
        }

        let isFirst = true;
        const guid = Guid.generateGuid()
        if (this.selectedCustomers && this.selectedCustomers.length > 0) {
            const customersSelected = this.selectedCustomers.filter(x => x.isSelected == true && (x.id > 0 || x.id == null)).map(x => x.id);
            if (customersSelected && customersSelected.length > 0) {
                if(customersSelected.filter(x => x != null).length > 0)
                {
                    const buFilter = QueryFilter.getQuery("customerId", customersSelected.filter(x => x != null).join(","), '{0} in ({1})', DataType.Lookup, isFirst, false, guid, null, true);
                    buFilter.filterProperty.lookupMappedToProperty = LookupValueQuery.Customers;

                    initialFilters.push(buFilter);
                    isFirst = false;
                }

                if(customersSelected && customersSelected.filter(x => x == null).length > 0)
                {
                    const nullBuFilter = QueryFilter.getQuery("customerId", "null", 'eq', DataType.Lookup, isFirst, this.selectedPrivateList == null, guid, null, false);
                    nullBuFilter.filterProperty.lookupMappedToProperty = LookupValueQuery.Customers;
                    initialFilters.push(nullBuFilter);
                    isFirst = false;
                }
            }
        }

        if (this.selectedPrivateList != null && this.selectedPrivateList != undefined) {
            const privateFilter = QueryFilter.getQuery("isPersonal", this.selectedPrivateList, 'eq', DataType.Boolean, isFirst, true, guid, null);
            initialFilters.push(privateFilter);
        }

        if (this.selectedCategoryIds && this.selectedCategoryIds.length > 0) {
            const categoryFilter = QueryFilter.getQuery("listGroupId", this.selectedCategoryIds.map(x => x.toString()).join(","), '{0} in ({1})', DataType.Lookup, true, false, null, null, true);
            categoryFilter.filterProperty.lookupMappedToProperty = LookupValueQuery.ListGroups;

            initialFilters.push(categoryFilter);

        }
        if (this.selectedLinkedToVetting != null && this.selectedLinkedToVetting != undefined) {
            initialFilters.push(QueryFilter.getQuery("isLinkedToVettingRule", this.selectedLinkedToVetting, "eq", DataType.Boolean, true));
        }

        if (this.isRightship != null && this.isRightship != undefined) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("customerId", "null", 'eq', DataType.Lookup, true, true, guid, null, false));
        }

        this.itemSource = {
            sourceType: SourceType.ODataUrl,
            source: "odata/Lists",
            columns: this.columns,
            initialFilters: initialFilters
        };
    }

    public addList() {
        this.editList(null);
    }

    public editList(item: any) {
        this.dialogClient.show(RightShipDialogServiceConstants.URLs.EditListDialog, {
            onComplete: () => {
                this.reload();
            },
            id: item ? item.id : null
        }, true, false, false, 'right');
    }

    showDeleteDialog(item) {
        this.dialogClient.show(RightShipDialogServiceConstants.URLs.ListDeleteDialog, { id: item.id, listName: item.name, deleteFunc: this.deleteList }, true, false, false, 'right');
    }

    deleteList = (id: number, reason: string) => {

        return new Promise<void>((resolve, reject) => {

            const request: DeleteListRequest = {
                id: id,
                reason: reason
            }

            this.listService.deleteList(request).then(response => {
                if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                    reject();
                } else {
                    this.notificationClientService.show(this.i18N.tr('successfully-deleted-text'), "", NotificationType.Success, 5000);
                    this.reload();
                    resolve();
                }
            }).catch(x => {
                const response: {
                    exception: string;
                    details: string;
                    validationResult: ValidationResult;
                } = typeof (x.response) === 'object'
                        ? x.response
                        : (x.response ? JSON.parse(x.response) : {});
                if (!response.exception || !response.details) {
                    this.notificationClientService.show(this.i18N.tr('failed-list-deletion-text'), "", NotificationType.Error, 5000);
                } else if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                }
                reject();
            });
        })
    };

    public selectedFilters: number[] = [];

    private filter(filter: number) {
        this.selectedFilters = [];

        if (filter) {
            this.selectedFilters.push(filter);
        }

        this.reload();
    }

    filterClick(filter: number) {
        return new Promise<void>((resolve, reject) => {
            //reset filters when tab is changed
            this.clearAllFilters();

            if (!this.initialPromise) {
                this.filter(filter);
                resolve();
                return;
            }

            this.initialPromise.then(() => {
                this.filter(filter);
                resolve();
            }, reject);
        });
    }

    @computedFrom('selectedFilters', 'selectedFilters.length')
    public get isCompanySelected() {
        return this.selectedFilters.indexOf(3) > -1
    }

    @computedFrom('selectedFilters', 'selectedFilters.length')
    public get isVesselSelected() {
        return this.selectedFilters.indexOf(2) > -1
    }


    //#region Filters

    //#region Search Filter
    @observable()
    public searchFilter: string = null;

    searchFilterChanged() {
        this.debounceReload(false);
    }

    searchKeyDown($event) {
        if ($event && $event.detail) {
            const code = $event.detail.keyCode;
            if (code === 13) {
                this.reload()
            }
        }
    }

    clearSearchFilter() {
        this.searchFilter = null;
    }
    //#endregion

    //#region Category Filter
    public showCategoryIdsFilter: boolean = false;

    @observable()
    public selectedCategoryIds: number[];

    @computedFrom("selectedCategoryIds", "selectedCategoryIds.length")
    public get categoryIdsFilterActive() {
        return this.selectedCategoryIds?.length > 0;
    }

    selectedCategoryIdsChanged() {
        this.debounceReload(false);
    }

    @computedFrom("selectedCategoryIds", "selectedCategoryIds.length")
    public get categoriesFilterActive() {
        return this.selectedCategoryIds?.length > 0;
    }

    public clearCategoryIdsFilter() {
        this.selectedCategoryIds = [];
        this.reload();
    }

    public applyCategoryIdsFilter(items: any[], reload: boolean = true) {
        this.selectedCategoryIds = items.map(x => x.id);
        if (reload == true) this.reload();
        this.toggleCategoryIdsFilters();
    }

    public toggleCategoryIdsFilters() {
        if (!this.showCategoryIdsFilter) {
            this.closeAllFilters();
        }
        this.showCategoryIdsFilter = !this.showCategoryIdsFilter;
        this.selectedFilter = {
            title: "Category Ids",
            clear: () => {
                this.clearCategoryIdsFilter();
            },
            apply: () => {
                this.applyCategoryIdsFilter(this.selectedCategoryIds.map(cId => {
                    return { id: cId };
                }), false);
            }
        }
    }

    @computedFrom('isRightShipUser')
    get listGroupLookupParams(): string {
        return this.isRightShipUser ? 'true,null': 'false,null';
    }

    //#endregion

    //#region Privacy filter
    public showCustomersFilter: boolean = false;

    @observable()
    public selectedCustomers: { name: string, id: number, isSelected: boolean }[] = [];

    @computedFrom("selectedCustomers", "selectedCustomers.length")
    public get customerFilterActive() {
        return this.selectedCustomers?.length > 0;
    }

    @computedFrom("selectedCustomers", "selectedCustomers.length")
    public get customersFilterActive() {
        return this.selectedCustomers?.length > 0;
    }

    selectedCustomersChanged() {
        this.debounceReload(false);
    }

    public clearCustomersFilter() {
        this.selectedCustomers = [];
        this.reload();
    }

    public applyCustomersFilter(items: any[], reload: boolean = true) {
        this.selectedCustomers = items;
        if (reload == true) this.reload();
        this.toggleCustomersFilters();
    }

    public toggleCustomersFilters() {
        if (!this.showCustomersFilter) {
            this.closeAllFilters();
        }
        this.showCustomersFilter = !this.showCustomersFilter;
        this.selectedFilter = {
            title: "Customers",
            clear: () => {
                this.clearCustomersFilter();
            },
            apply: () => {
                this.applyCustomersFilter(this.selectedCategoryIds.map(cId => {
                    return { id: cId };
                }), false);
            }
        }
    }

    customers: { name: string, id: number }[] = []

    setPrivacies(c: LookupValueResponse) {

        const values = c.activeValues;

        let customers = [];
        if (this.isRightShipUser) {
            customers = [{ name: "RightShip System List", id: null }].concat(values.map(x => {
                return { name: x.name, id: x.id };
            }));
        } else {
            customers = values.filter(x => x.id == this.customerId).map(x => {
                return { name: x.name, id: x.id };
            });
        }

        this.customers = customers;
    }
    //#endregion

    //#region Linked To Vetting Rules filter
    @observable()
    selectedLinkedToVetting: boolean = null;

    selectedLinkedToVettingChanged() {
        if (this.selectedLinkedToVetting == false) {
            this.selectedLinkedToVetting = null;
        }
        this.debounceReload(false);
    }

    /**
     * 'selectedPrivateList' filter clicked handler
     * @param isMobile Is mobile filter?
     */
    selectedLinkedToVettingClicked(isMobile: boolean = false) {
        this.selectedLinkedToVetting = !this.selectedLinkedToVetting;
        if (!isMobile) this.reload();
    }
    //#endregion

    //#region Private List filter
    @observable()
    selectedPrivateList: boolean = null;

    selectedPrivateListChanged() {
        if (this.selectedPrivateList == false) {
            this.selectedPrivateList = null;
        }
        this.debounceReload(false);
    }

    /**
     * 'selectedPrivateList' filter clicked handler
     * @param isMobile Is mobile filter?
     */
    selectedPrivateListClicked(isMobile: boolean = false) {
        this.selectedPrivateList = !this.selectedPrivateList;
        if (!isMobile) this.reload();
    }
    //#endregion

    //#region IsRightshgip filter
    @observable()
    isRightship: boolean = null;
 
    isRightshipChanged() {
        if (this.isRightship == false) {
            this.isRightship = null;
        }
        this.debounceReload(false);
    }

    /**
     * 'isRightship' filter clicked handler
     * @param isMobile Is mobile filter?
     */
    isRightshipClicked(isMobile: boolean = false) {
        this.isRightship = !this.isRightship;
        if (!isMobile) this.reload();
    }
    //#endregion

    /**
     * Clear all selected filter values
     */
    clearAllFilters(reload: boolean = true) {
        if(this.dataGridVM)
        {
            this.dataGridVM.clearSorting();
        }
        this.searchFilter = null;
        this.selectedCustomers = [];
        this.selectedCategoryIds = [];
        this.selectedLinkedToVetting = null;
        this.selectedPrivateList = null;
        this.isRightship = null;

        if (reload)
            this.reload();
    }

    public mobileFilterApply(toggleDialog: boolean = true) {
        if(toggleDialog)
            this.toggleMobileFilterDialog();
    }

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        this.closeAllFilters();
    }

    public get isSubFilterOpen(): boolean {
        return this.showCustomersFilter || this.showCategoryIdsFilter;
    }

    /**
     * Close any open filters
     */
    public closeAllFilters() {
        this.showCategoryIdsFilter = false;
        this.showCustomersFilter = false;
    }

    public mobileApplySelectedFilter() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
        }
    }

    hideMobileFilters() {
        this.isMobileFilterOpen = false;
    }

    /**
     * Return 'true' if any filter is selected
     */
    @computedFrom('selectedCategoryIds', 'selectedCustomers', 'selectedPrivateList', 'selectedLinkedToVetting')
    get filtersSelected() {
        return this.selectedCategoryIds.length || this.selectedCustomers.length || this.selectedPrivateList || this.selectedLinkedToVetting;
    }

    //#endregion
}
